import React, { useState, useRef, useEffect } from 'react';
import './HelpTooltip.css';
import infoIcon from '../../images/icons/info_icon.png';

interface HelpTooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: string | React.ReactNode;
  
  /**
   * Position of the tooltip relative to the icon
   * @default 'top'
   */
  position?: 'top' | 'right' | 'bottom' | 'left';
  
  /**
   * Additional CSS class for the tooltip container
   */
  className?: string;
  
  /**
   * Width of the tooltip popup
   * @default '250px'
   */
  width?: string;
  
  /**
   * Accessibility label for the tooltip icon
   * @default 'Help'
   */
  ariaLabel?: string;
}

/**
 * HelpTooltip component
 * 
 * A reusable component that displays a help icon which, when clicked,
 * shows a tooltip with customizable content.
 */
const HelpTooltip: React.FC<HelpTooltipProps> = ({
  content,
  position = 'top',
  className = '',
  width = '250px',
  ariaLabel = 'Help'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current && 
        !tooltipRef.current.contains(event.target as Node) &&
        iconRef.current &&
        !iconRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close tooltip
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const toggleTooltip = () => {
    setIsOpen(!isOpen);
  };

  const closeTooltip = () => {
    setIsOpen(false);
  };

  return (
    <div className={`help-tooltip-container ${className}`}>
      <button
        ref={iconRef}
        className="help-tooltip-icon"
        onClick={toggleTooltip}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        type="button"
      >
        <img src={infoIcon} alt="" className="info-icon" />
      </button>
      
      {isOpen && (
        <div 
          ref={tooltipRef}
          className={`help-tooltip-popup help-tooltip-${position}`}
          role="dialog"
          aria-label={`${ariaLabel} information`}
          style={{ width }}
        >
          <div className="help-tooltip-content">
            {content}
          </div>
          <button 
            className="help-tooltip-close"
            onClick={closeTooltip}
            aria-label="Close tooltip"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default HelpTooltip;